import React, { useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const { BaseLayer } = LayersControl;

const markerBaseUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/';
const shadowUrl = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png';
const markerColors = ['red', 'green', 'blue', 'orange', 'yellow', 'violet', 'grey', 'black'];

const createLeafletIcon = (color) =>
  new L.Icon({
    iconUrl: `${markerBaseUrl}marker-icon-${color}.png`,
    shadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

const MapView = ({ data = [] }) => {
  // ✅ STEP 1: Filter only valid coordinates
  const validData = useMemo(() => {
    return data
      .map((loc) => {
        if (!loc.coordinate || !loc.coordinate.includes(',')) return null;
        const [latStr, lngStr] = loc.coordinate.split(',');
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (isNaN(lat) || isNaN(lng)) return null;
        return { ...loc, lat, lng };
      })
      .filter(Boolean); // Remove nulls
  }, [data]);

  // ✅ STEP 2: Create icon/color map and count from only valid data
  const { iconMap, pillarColorMap, pillarCountMap } = useMemo(() => {
    const pillarColorMap = new Map();
    const iconMap = new Map();
    const pillarCountMap = new Map();
    let colorIndex = 0;

    validData.forEach((loc) => {
      const pillar = loc.pillar || 'Unknown';
      // Count
      pillarCountMap.set(pillar, (pillarCountMap.get(pillar) || 0) + 1);
      // Color/icon
      if (!pillarColorMap.has(pillar)) {
        const color = markerColors[colorIndex % markerColors.length];
        pillarColorMap.set(pillar, color);
        iconMap.set(pillar, createLeafletIcon(color));
        colorIndex++;
      }
    });

    return { iconMap, pillarColorMap, pillarCountMap };
  }, [validData]);

  return (
    <div>
      <MapContainer
        center={[6.18, 46.5]}
        maxBounds={[[2.0, 46], [10.2, 48.0]]}
        maxBoundsViscosity={1.0}
        zoom={8}
        style={{
          height: '600px',
          width: '100%',
          border: '3px solid blue',
          borderRadius: '12px',
          boxShadow: '0 0 10px rgba(143, 130, 130, 0.2)',
        }}
      >
        <LayersControl position="topright">
          <BaseLayer checked name="Street View">
            <TileLayer
              url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors, Tiles style by <a href="https://openstreetmap.fr/">OpenStreetMap France</a>'
            />
          </BaseLayer>
          <BaseLayer name="Satellite View">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution="© Google"
            />
          </BaseLayer>
        </LayersControl>

        {validData.map((loc) => {
          const icon = iconMap.get(loc.pillar || 'Unknown');
          return (
            <Marker
              key={loc.id || `${loc.lat},${loc.lng}`}
              position={[loc.lat, loc.lng]}
              icon={icon}
            >
              <Popup>
                {Object.entries(loc).map(([key, value]) =>
                  !['id', 'coordinate', 'lat', 'lng'].includes(key) ? (
                    <div key={key}>
                      <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong> {value}<br />
                    </div>
                  ) : null
                )}
                <div style={{ marginTop: '5px' }}>
                  <a
                    href={`https://earth.google.com/web/search/${loc.lat},${loc.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🌍 View in Google Earth
                  </a>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* ✅ Final Accurate Pillar Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginTop: '10px',
        padding: '10px',
        border: '2px solid #ccc',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <strong style={{ flexBasis: '100%', marginBottom: '6px' }}>Colors (Valid Coordinates Only):</strong>
        {[...pillarColorMap.entries()].map(([pillar, color]) => (
          <div key={pillar} style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={`${markerBaseUrl}marker-icon-${color}.png`}
              alt={color}
              style={{ width: '20px', height: '34px', marginRight: '5px' }}
            />
            <span>{pillar} ({pillarCountMap.get(pillar)} project{pillarCountMap.get(pillar) > 1 ? 's' : ''})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MapView;
